import * as React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Profile, ProfileFormData } from '../types';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import { ProfilesService } from '../services/profiles-service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

export function EditProfileFormPage() {
  const { id } = useParams({ from: '/_authenticated/profiles/edit/$id' });
  const [profile, setProfile] = React.useState<Profile | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  
  // Form state
  const [formData, setFormData] = React.useState<ProfileFormData>({
    profile_full_name: '',
    profile_title: '',
    profile_photo: '',
    profile_summary: '',
    profile_bio: '',
    profile_position_type: 'executive_team',
    profile_order_weight: 999,
    profile_show_active: true
  });

  const navigate = useNavigate();

  React.useEffect(() => {
    // Fetch profile from API
    setIsLoading(true);
    ProfilesService.getProfileById(id)
      .then(profileData => {
        setProfile(profileData);
        // Initialize form data with profile data
        setFormData({
          profile_full_name: profileData.profile_full_name,
          profile_title: profileData.profile_title,
          profile_photo: profileData.profile_photo || '',
          profile_summary: profileData.profile_summary || '',
          profile_bio: profileData.profile_bio || '',
          profile_position_type: profileData.profile_position_type,
          profile_order_weight: profileData.profile_order_weight,
          profile_show_active: profileData.profile_show_active
        });
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error fetching profile:', error);
        setProfile(null);
        setIsLoading(false);
        toast.error('Failed to load profile');
      });
  }, [id]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle select changes
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle switch changes
  const handleSwitchChange = (name: string, checked: boolean) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    
    try {
      console.log('Submitting profile data:', formData);
      
      // Call API to update profile
      const updatedProfile = await ProfilesService.updateProfile(id, formData);
      
      console.log('Profile updated successfully:', updatedProfile);
      toast.success('Profile updated successfully');
      
      // Navigate to the profile details page
      navigate({ to: '/profiles/$id', params: { id } });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
      
      // Show more detailed error message
      if (error instanceof Error) {
        alert(`Error updating profile: ${error.message}`);
      } else {
        alert('An unknown error occurred while updating the profile');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate({ to: '/profiles/$id', params: { id } });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <Alert variant="destructive">
        <Info className="h-4 w-4" />
        <AlertTitle>Profile Not Found</AlertTitle>
        <AlertDescription>
          The profile with ID {id} could not be found. It may have been deleted or you may not have permission to view it.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Edit Profile: ${profile.profile_full_name}`}
        description="Update profile information and settings."
      />
      
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="profile_full_name">Full Name *</Label>
                  <Input
                    id="profile_full_name"
                    name="profile_full_name"
                    value={formData.profile_full_name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="profile_title">Title *</Label>
                  <Input
                    id="profile_title"
                    name="profile_title"
                    value={formData.profile_title}
                    onChange={handleInputChange}
                    placeholder="Chief Executive Officer"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="profile_position_type">Position Type *</Label>
                  <Select
                    value={formData.profile_position_type}
                    onValueChange={(value) => handleSelectChange('profile_position_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select position type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="executive_team">Executive Team</SelectItem>
                      <SelectItem value="board_member">Board Member</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="profile_order_weight">Display Order</Label>
                  <Input
                    id="profile_order_weight"
                    name="profile_order_weight"
                    type="number"
                    value={formData.profile_order_weight.toString()}
                    onChange={handleInputChange}
                    placeholder="999"
                  />
                  <p className="text-xs text-muted-foreground">Lower numbers appear first</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="profile_photo">Photo URL</Label>
                <Input
                  id="profile_photo"
                  name="profile_photo"
                  value={formData.profile_photo}
                  onChange={handleInputChange}
                  placeholder="/profiles/john_doe.jpg"
                />
                <p className="text-xs text-muted-foreground">
                  Enter the path to the profile photo. For example: /profiles/john_doe.jpg
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="profile_summary">Summary</Label>
                <Textarea
                  id="profile_summary"
                  name="profile_summary"
                  value={formData.profile_summary || ''}
                  onChange={handleInputChange}
                  placeholder="Brief summary of the person's role and experience"
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="profile_bio">Full Biography</Label>
                <Textarea
                  id="profile_bio"
                  name="profile_bio"
                  value={formData.profile_bio || ''}
                  onChange={handleInputChange}
                  placeholder="Detailed biography with professional experience and background"
                  rows={6}
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="profile_show_active"
                  checked={formData.profile_show_active}
                  onCheckedChange={(checked) => handleSwitchChange('profile_show_active', checked)}
                />
                <Label htmlFor="profile_show_active">Active</Label>
              </div>
            </div>
            
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <span className="animate-spin mr-2">⟳</span> Saving...
                  </>
                ) : (
                  'Update Profile'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default EditProfileFormPage;
